import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { PortalContext } from '../../../context/PortalContext';
import { useLagoon } from '../../../hooks/useLagoon';
import { shouldUseGehaV2Styling } from '../../../utils/gehaThemeUtils';
import { getLagoonConfigValue } from '../../../utils/providerSearch.utils';
import { mobileOnly } from '../../ConstantsStyles';
import {
  A11yHiddenSpan,
  IconContainer,
  Input,
  KeywordSearchInputContainer,
  KeywordSearchLabel,
  KeywordSearchStyledDiv,
  SearchPlacementStyle,
} from './SearchBar.styled';

type Props = {
  keywordSearchInputId: string;
  searchLabelText: any;
  choosePCPHeader?: boolean;
  isFocusedOnKeywordSearchInput: any;
  isFocusedOnLocationSearch: any;
  search?: string | null;
  setResults: any;
  setSearchButtonResults: any;
  handleKeywordSearchInputChange: any;
  handleKeywordSearchInputFocus: any;
  handleKeywordSuggestionKeyDown: any;
  searchInputRef: any;
  keywordSearchTerm: any;
  searchButtonResults: any;
  commonSearchesData: any;
  dentalCoverage: any;
  visionCoverage: any;
};
export const SearchPlacement = ({
  keywordSearchInputId,
  searchLabelText,
  choosePCPHeader,
  isFocusedOnKeywordSearchInput,
  isFocusedOnLocationSearch,
  search,
  setResults,
  setSearchButtonResults,
  handleKeywordSearchInputChange,
  handleKeywordSearchInputFocus,
  handleKeywordSuggestionKeyDown,
  searchInputRef,
  keywordSearchTerm,
  searchButtonResults,
  commonSearchesData,
  dentalCoverage,
  visionCoverage,
}: Props) => {
  const mobileScreen = useMediaQuery(mobileOnly);
  const { portalData } = useContext(PortalContext);
  const [searchInput, setSearchInput] = useState(search || '');

  const allConfigs = useLagoon('config');
  const forbiddenCharactersTypeahead = getLagoonConfigValue(
    allConfigs,
    ConstantsLagoon.CONFIG.FORBIDDEN_CHARACTERS_TYPEAHEAD
  );

  useEffect(() => {
    setSearchInput(search || '');
  }, [search]);

  useEffect(() => {
    const shouldResetSearchInput =
      !isFocusedOnKeywordSearchInput && !isFocusedOnLocationSearch;
    if (shouldResetSearchInput) {
      setSearchInput(search || '');
    }
  }, [isFocusedOnKeywordSearchInput, isFocusedOnLocationSearch]);

  const { t } = useTranslation();
  const themeOverride = portalData?.portalConfig;
  const icColor = themeOverride
    ? '$searchIconColor'
    : choosePCPHeader && mobileScreen && '$core.color.brand.60';
  const isGEHAV2 = shouldUseGehaV2Styling();

  const sanitizeString = (input: string): string =>
    input.replace(/\s+/g, '').replace(/[\\\[\]{}()*+?.^$|]/g, '\\$&');

  return (
    <SearchPlacementStyle>
      <KeywordSearchInputContainer>
        <KeywordSearchLabel
          cssProps={{ isGEHAV2 }}
          data-testid="provider-search-label"
          htmlFor={keywordSearchInputId}
        >
          {searchLabelText?.message ?? ''}
        </KeywordSearchLabel>
        <KeywordSearchStyledDiv>
          <IconContainer>
            <IconSymbol
              color={icColor}
              icon="search"
              size={mobileScreen ? 20 : 24}
            />
          </IconContainer>

          <Input
            aria-activedescendant={
              isFocusedOnKeywordSearchInput
                ? 'primary-search-bar-result-suggestion'
                : ''
            }
            aria-autocomplete="list"
            aria-controls="primary-search-bar-suggestions"
            aria-expanded={isFocusedOnKeywordSearchInput}
            aria-haspopup="listbox"
            autoComplete="off"
            data-testid="primary-search-input"
            id={keywordSearchInputId}
            key={search}
            onChange={(e) => {
              const forbiddenCharsRegex = new RegExp(
                `[${sanitizeString(forbiddenCharactersTypeahead ?? '')}]`
              );
              if (forbiddenCharsRegex.test(e.target.value)) {
                return;
              }
              setSearchInput(e.target.value);
              if (
                e.target.value?.trim().length <= 1 ||
                !/[A-Za-z0-9]/.test(e.target.value?.trim())
              ) {
                setResults([]);
                setSearchButtonResults([]);
              }

              handleKeywordSearchInputChange(e);
            }}
            onFocus={handleKeywordSearchInputFocus}
            onKeyDown={handleKeywordSuggestionKeyDown}
            placeholder={t('SEARCH_PLACEHOLDER')}
            ref={searchInputRef}
            role="combobox"
            type="text"
            value={t(searchInput)}
          />

          <A11yHiddenSpan aria-atomic="true" aria-live="polite">
            {`${
              keywordSearchTerm?.trim().length > 1
                ? searchButtonResults.length + 2
                : commonSearchesData.length +
                  (dentalCoverage ? 1 : 0) +
                  (visionCoverage ? 1 : 0)
            } ${t('results available')}`}
          </A11yHiddenSpan>
        </KeywordSearchStyledDiv>
      </KeywordSearchInputContainer>
    </SearchPlacementStyle>
  );
};
